import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../help.css';

/*
 * In-app Help / tutorial page.
 *
 * One smart, platform-aware page: it auto-detects phone vs computer (the same
 * `min(w,h) < 550` test App.js uses to pick MobileView) and defaults to that
 * platform's instructions, with a Desktop / Mobile toggle so either can be read.
 *
 * Screenshots live in frontend/public/help/ and are captured straight from the
 * running app. If the UI changes materially, re-capture them (the scratch
 * puppeteer script that produced them drives the live app with a test token).
 */

const isPhone = () => Math.min(window.innerWidth, window.innerHeight) < 550;

// Each feature: how it works + a real screenshot, per platform.
// availability: 'both' | 'desktop'  (Coding Labs is desktop-only).
const FEATURES = [
  {
    id: 'navigation',
    icon: '🧭',
    title: 'Finding your way around',
    summary: 'How to move between the app’s main areas.',
    teacherNote: 'As a teacher, every unit, exercise and topic is open to you straight away — nothing is locked and you don’t need to complete anything first, so you can preview exactly what your students will see.',
    desktop: {
      img: 'desktop-drawer.png',
      steps: [
        'Open the menu with the ☰ button at the top-left to jump to Coding Labs, Theory Review, the CPU Simulator, Packet Switching, Towers of Hanoi, Fractal Drawing, or any unit in the curriculum.',
        'The top bar shows your level and XP; click it to open your dashboard.',
        'Use the 🌙 / ☀️ button to switch between light and dark mode, and the avatar menu to view your profile or log out.',
      ],
    },
    mobile: {
      img: 'mobile-drawer.png',
      steps: [
        'Tap the ☰ button at the top-left for the CPU Simulator, Packet Switching, Towers of Hanoi, Fractal Drawing, this Help guide, and log out.',
        'Switch between the two main areas with the 📚 Theory Review and 🎒 The Locker tabs.',
        'Tap the 🌙 / ☀️ button any time to switch between light and dark mode.',
      ],
    },
  },
  {
    id: 'coding-labs',
    icon: '💻',
    title: 'Coding Labs (Theme B)',
    summary: 'Write and run real code, checked against milestones.',
    availability: 'desktop',
    teacherNote: 'Teachers can open any exercise in any unit without finishing the earlier ones, and Paper 2 is unlocked for you from the start — so you can jump straight to whatever you want to demonstrate. Your own XP and badges still only come from exercises you actually complete.',
    desktop: {
      img: 'desktop-coding.png',
      steps: [
        'Open the Coding Labs tab on your dashboard to see the Syllabus Matrix, every Theme B unit with your progress bar.',
        'Pick a unit to open the exercise: instructions on the left, a code editor in the middle, and a terminal for the output.',
        'Switch between Python and Java with the language toggle, and choose SL or HL with the track toggle.',
        'Press Run to test your code against the exercise’s milestones. Each one you pass earns XP and raises your mastery.',
      ],
    },
    mobile: {
      img: 'desktop-coding.png',
      note: 'Coding Labs runs on a computer. On your phone, use Theory Review and The Locker to revise, then switch to a laptop or desktop to write and run code.',
      steps: [],
    },
  },
  {
    id: 'brookebits',
    icon: '🧩',
    title: 'brookebits',
    summary: 'A scratchpad to try code, plus saveable, shareable code bits.',
    teacherNote: 'brookebits is the quickest way to demonstrate an idea: open a scratchpad, write a few lines and Run them live in front of the class. Save anything worth keeping as a bit, then use Share to hand students a link they can open and Fork into their own copy to experiment with.',
    desktop: {
      steps: [
        'Open brookebits from the ☰ menu (or go straight to /bits). Start with New Python scratchpad or New Java scratchpad — a blank canvas to write and run code, with nothing saved until you choose to.',
        'Type your code and press Run to execute it in the terminal on the right, just like Coding Labs but with a free-form editor. input() prompts work in the terminal.',
        'Press Save to turn a scratchpad into a bit: it gets a title and its own link, and appears under My bits. Reopen or delete your bits from there (each account has a generous bit limit).',
        'Use Share to copy a link. Anyone signed in can open it, and Fork it to get their own editable copy — handy for sharing a starting point or a worked example.',
      ],
    },
    mobile: {
      note: 'brookebits works on a phone, but the code editor is much easier to use on a larger screen — switch to a laptop or desktop for longer sessions.',
      steps: [
        'Tap the ☰ menu and choose brookebits, then tap New Python scratchpad or New Java scratchpad to write and run code.',
        'Tap Save to keep a scratchpad as a bit, and Share to copy a link others can open and fork.',
      ],
    },
  },
  {
    id: 'theory-review',
    icon: '📚',
    title: 'Theory Review (Theme A)',
    summary: 'Revise the theory with flashcards, quizzes and SQL.',
    desktop: {
      img: 'desktop-theory.png',
      steps: [
        'Open the Theory Review tab and expand any topic in the accordion (e.g. A3 Databases).',
        'Tick the statements you want to study; the toolbar shows how many you’ve selected.',
        'Launch Flashcards, Quiz, or (for A3.3) SQL from the selection toolbar.',
        'The Review … due button surfaces cards spaced repetition says you’re about to forget, based on how you rated them before.',
      ],
    },
    mobile: {
      img: 'mobile-theory-cta.png',
      steps: [
        'On the 📚 Theory Review tab, tap a topic to expand it, then tap a sub-topic’s checkbox to select its statements.',
        'A bar slides up from the bottom with Flashcards, Quiz, and (for A3.3) SQL.',
        'Tap Review … due at the top to revise the cards you’re about to forget.',
      ],
    },
  },
  {
    id: 'flashcards',
    icon: '🃏',
    title: 'Flashcards',
    summary: 'Flip-card revision with spaced repetition.',
    desktop: {
      img: 'desktop-flashcards.png',
      steps: [
        'Read the prompt, recall your answer, then flip the card to check.',
        'Rate how well you knew it, and the app schedules each card to come back just before you’d forget it.',
      ],
    },
    mobile: {
      img: 'mobile-flashcards.png',
      steps: [
        'Tap the card to flip it, then rate how well you knew the answer.',
        'Swipe through the deck; your ratings feed the spaced-repetition schedule.',
      ],
    },
  },
  {
    id: 'quiz',
    icon: '✅',
    title: 'Quiz',
    summary: 'Multiple-choice checks that bank XP and mastery.',
    desktop: {
      img: 'desktop-quiz.png',
      steps: [
        'Pick an answer, and you get instant feedback on whether it’s right.',
        'Correct answers add XP and nudge that statement’s mastery upward.',
      ],
    },
    mobile: {
      img: 'mobile-quiz.png',
      steps: [
        'Tap an answer to lock it in and see instant feedback.',
        'Work through the set; your score updates your mastery for those statements.',
      ],
    },
  },
  {
    id: 'sql',
    icon: '🗄️',
    title: 'SQL exercises',
    summary: 'Write and run real SQL right in the browser (A3.3).',
    desktop: {
      img: 'desktop-sql.png',
      steps: [
        'Read the task, then type your query into the editor (it’s seeded with a starting point).',
        'Press Run, and your SQL executes against a real in-browser database and the results appear below.',
        'A green Correct! confirms a pass and banks XP; use Previous / Next to move through the set.',
      ],
    },
    mobile: {
      img: 'mobile-sql.png',
      steps: [
        'Tap the editor to write your query, then tap Run.',
        'The query runs against a real in-browser database and shows the result grid plus a pass/fail verdict.',
      ],
    },
  },
  {
    id: 'cpu',
    icon: '🖥️',
    title: 'CPU Simulator',
    summary: 'Step through the fetch-decode-execute cycle (Little Man Computer).',
    desktop: {
      img: 'desktop-cpu.png',
      steps: [
        'Choose a preloaded program or write your own assembly, then press Assemble.',
        'Use Step, Step instr. or Run to watch values move along the address, data and control buses.',
        'Watch the registers (PC, MAR, MDR, IR, Accumulator) and memory mailboxes update on every cycle.',
      ],
    },
    mobile: {
      img: 'desktop-cpu.png',
      note: 'Open it from the ☰ menu. It’s a wide tool: turn your phone sideways or use a larger screen for the full layout.',
      steps: [
        'Choose a preloaded program or write assembly, then press Assemble.',
        'Use Step or Run to watch the registers, buses and memory update each cycle.',
      ],
    },
  },
  {
    id: 'packets',
    icon: '🌐',
    title: 'Packet Switching',
    summary: 'See how a message is split into packets and routed across a network.',
    desktop: {
      img: 'desktop-packets.png',
      steps: [
        'Open Packet Switching from the menu and start the simulation.',
        'Follow individual packets as they take different routes and are reassembled at the destination.',
      ],
    },
    mobile: {
      img: 'desktop-packets.png',
      note: 'Open it from the ☰ menu. Best viewed on a wider screen.',
      steps: [
        'Start the simulation from the ☰ menu and watch packets travel across the network.',
      ],
    },
  },
  {
    id: 'hanoi',
    icon: '🗼',
    title: 'Towers of Hanoi',
    summary: 'Learn recursion (B2.4.4, HL) by solving the classic disk puzzle.',
    desktop: {
      img: 'desktop-hanoi.png',
      steps: [
        'Pick how many disks to start with, then drag one disk at a time between the three pegs. You can never place a larger disk on a smaller one.',
        'Move the whole tower from peg A to peg C; your move count is compared live against the minimum possible (2ⁿ − 1).',
        'Open the “How recursion cracks this puzzle” hint for a step-by-step diagram showing how the problem breaks into smaller, identical subproblems.',
      ],
    },
    mobile: {
      img: 'desktop-hanoi.png',
      note: 'Open it from the ☰ menu. Disks are drag-and-drop, so it works on touch too.',
      steps: [
        'Drag disks between the pegs to move the whole tower from A to C without stacking a larger disk on a smaller one.',
        'Expand the recursion hint to see how the solution breaks into smaller subproblems.',
      ],
    },
  },
  {
    id: 'fractals',
    icon: '🔺',
    title: 'Fractal Drawing',
    summary: 'See recursion draw self-similar fractals (B2.4.4, HL).',
    desktop: {
      img: 'desktop-fractals.png',
      steps: [
        'Choose a fractal (Sierpiński triangle, Koch snowflake, recursion tree, or Pythagoras tree), each drawn by a recursive function.',
        'Drag the recursion-depth slider and watch self-similar detail appear as the shape splits into smaller copies of itself.',
        'Watch the live “shapes drawn” stat grow exponentially, and open the recursion hint for the base case, recursive case and pseudocode.',
      ],
    },
    mobile: {
      img: 'desktop-fractals.png',
      note: 'Open it from the ☰ menu. Best viewed on a wider screen.',
      steps: [
        'Pick a fractal and slide the recursion depth up to watch the self-similar pattern build.',
        'Expand the recursion hint to see the base case, recursive case and why the shape count grows exponentially.',
      ],
    },
  },
  {
    id: 'locker',
    icon: '🎒',
    title: 'The Locker',
    summary: 'Your XP, level, streaks, badges and avatar cosmetics.',
    desktop: {
      img: 'desktop-locker.png',
      steps: [
        'Open The Locker tab to see your Stats (clean runs, exercises completed, streak), Badges, and Cosmetics.',
        'XP from coding and theory feeds one shared level across both themes; spend progress on avatar cosmetics.',
      ],
    },
    mobile: {
      img: 'mobile-locker.png',
      steps: [
        'Tap the 🎒 The Locker tab to see your stats, badges, and cosmetics.',
        'Your level is shared across coding and theory, so everything you do anywhere counts.',
      ],
    },
  },
  {
    id: 'settings',
    icon: '⚙️',
    title: 'Settings & toggles',
    summary: 'Tailor the app to how you study.',
    desktop: {
      steps: [
        'SL / HL track: hides or shows HL-only content so you only see what’s on your syllabus.',
        'Python / Java: switch the language for all of Coding Labs.',
        'Light / dark theme: the 🌙 / ☀️ button in the top bar.',
      ],
    },
    mobile: {
      steps: [
        'SL / HL track: the toggle above the topic list filters HL-only content.',
        'Light / dark theme: the 🌙 / ☀️ button in the top bar.',
      ],
    },
  },
];

// Teacher-only sections, shown at the top of the guide for teacher accounts.
const TEACHER_FEATURES = [
  {
    id: 'teacher-classes',
    icon: '🎓',
    title: 'Set up a class',
    summary: 'Create a class and add your students.',
    teacherOnly: true,
    steps: [
      'Open the ☰ menu and choose My classes (this only appears for teacher accounts).',
      'Click + New to create a class and give it a name, e.g. “DP1 HL 2026”.',
      'Set the class’s Language (Python / Java) and Level (SL / HL) with the toggles — this scopes the progress views to what your students actually do. It does not restrict what students can explore in the app.',
      'Paste your students’ emails into the Add students box and press Add students. Students who already have an account are added instantly with their progress intact; new ones are emailed an invite. You get a summary of who was invited, added, or couldn’t be added (and why).',
      'On the roster, use Resend to re-send an invite, Remove to take a student off (their account and progress are kept), or Move… to shift a student to another of your classes.',
    ],
  },
  {
    id: 'teacher-progress',
    icon: '📊',
    title: 'Track progress',
    summary: 'See how your class and each student are doing.',
    teacherOnly: true,
    steps: [
      'In My classes, pick a class and use the tabs: Roster, Coding, Theory, and Focus.',
      'Coding and Theory show a grid of every student against the units / topics — exercises passed, or quiz accuracy — shaded so strong and weak areas stand out at a glance.',
      'Focus lists the exercises and statements your class is struggling with most (scoring below 70%), so you can see exactly what to reteach.',
      'Click any student’s name to open their breakdown: their stuck points and progress across every unit and topic.',
      'The Language / Level toggles scope everything to the class’s track (for an SL class, HL-only material is hidden).',
    ],
  },
];

function BackArrow() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="10 3 5 8 10 13" />
    </svg>
  );
}

export default function Help({ onBack, isTeacher = false }) {
  // Teachers get the class-management sections at the top of the guide.
  const features = isTeacher ? [...TEACHER_FEATURES, ...FEATURES] : FEATURES;
  const [platform, setPlatform] = useState(() => (isPhone() ? 'mobile' : 'desktop'));
  const [activeId, setActiveId] = useState(features[0].id);
  const sectionRefs = useRef({});

  const jumpTo = useCallback((id) => {
    const el = sectionRefs.current[id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Highlight the section nearest the top as you scroll.
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.dataset.featureId);
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    Object.values(sectionRefs.current).forEach(el => el && obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="help-page">
      {/* Sticky header */}
      <header className="help-topbar">
        <button className="help-back" onClick={onBack}>
          <BackArrow /> Back
        </button>
        <div className="help-topbar-title">Help &amp; Guide</div>
        <div className="help-platform-toggle" role="tablist" aria-label="Platform">
          <button
            role="tab"
            aria-selected={platform === 'desktop'}
            className={`help-plat-btn${platform === 'desktop' ? ' help-plat-on' : ''}`}
            onClick={() => setPlatform('desktop')}
          >
            💻 Desktop
          </button>
          <button
            role="tab"
            aria-selected={platform === 'mobile'}
            className={`help-plat-btn${platform === 'mobile' ? ' help-plat-on' : ''}`}
            onClick={() => setPlatform('mobile')}
          >
            📱 Mobile
          </button>
        </div>
      </header>

      {/* Hero */}
      <div className="help-hero">
        <h1 className="help-hero-title">How to use brookebit</h1>
        <p className="help-hero-sub">
          Everything the app can do, and how to use it on a{' '}
          {platform === 'desktop' ? 'computer' : 'phone'}. Switch the toggle
          above to see the other device’s steps.
        </p>
      </div>

      <div className="help-layout">
        {/* Section nav */}
        <nav className="help-nav" aria-label="Help sections">
          {features.map(f => (
            <button
              key={f.id}
              className={`help-nav-item${activeId === f.id ? ' help-nav-active' : ''}`}
              onClick={() => jumpTo(f.id)}
            >
              <span className="help-nav-icon" aria-hidden="true">{f.icon}</span>
              <span className="help-nav-label">{f.title}</span>
            </button>
          ))}
        </nav>

        {/* Feature cards */}
        <main className="help-content">
          {features.map(f => {
            // Teacher sections carry platform-agnostic top-level steps; the
            // student features carry per-platform (desktop/mobile) blocks.
            const data = f[platform] || (f.steps ? { steps: f.steps } : {});
            const desktopOnlyHere = f.availability === 'desktop' && platform === 'mobile';
            return (
              <section
                key={f.id}
                id={f.id}
                data-feature-id={f.id}
                ref={el => { sectionRefs.current[f.id] = el; }}
                className="help-card"
              >
                <div className="help-card-head">
                  <span className="help-card-icon" aria-hidden="true">{f.icon}</span>
                  <div>
                    <h2 className="help-card-title">{f.title}</h2>
                    <p className="help-card-summary">{f.summary}</p>
                  </div>
                  {f.availability === 'desktop' && (
                    <span className="help-badge" title="Available on desktop only">Desktop only</span>
                  )}
                  {f.teacherOnly && (
                    <span className="help-badge help-badge-teacher" title="For teacher accounts">Teacher</span>
                  )}
                </div>

                {isTeacher && f.teacherNote && (
                  <div className="help-note help-note-teacher">👩‍🏫 {f.teacherNote}</div>
                )}

                {data.note && (
                  <div className="help-note">{data.note}</div>
                )}

                {data.img && (
                  <figure className={`help-shot help-shot-${platform}`}>
                    <img
                      src={`/help/${data.img}`}
                      alt={`${f.title} on ${platform}`}
                      loading="lazy"
                    />
                    {desktopOnlyHere && (
                      <figcaption className="help-shot-cap">Shown on desktop</figcaption>
                    )}
                  </figure>
                )}

                {data.steps && data.steps.length > 0 && (
                  <ol className="help-steps">
                    {data.steps.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ol>
                )}
              </section>
            );
          })}

          <p className="help-foot">
            Still stuck? If anything does not work as described here, please flag
            it so it can be fixed.
          </p>
        </main>
      </div>
    </div>
  );
}
